import React, { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import {
  enablePushNotifications,
  isPushSupported,
  isPushConfigured,
  currentPermission,
  hasOptedInBefore,
} from "../lib/pushClient";
import { useI18n } from "../contexts/I18nContext";

/**
 * Soft-ask notification opt-in banner.
 *
 * Behaviour:
 *   • Only shown when push is supported, configured, permission is `default`,
 *     and the user hasn't opted in on this device before.
 *   • Once dismissed, we remember the choice for 3 days so we don't spam.
 *   • On accept, we call enablePushNotifications() which triggers the
 *     browser's native permission prompt AND registers the FCM token.
 *
 * Mounted on /dashboard so it appears exactly once for engaged users
 * (not on the anonymous landing page — that would kill the conversion rate).
 */
const DISMISS_KEY = "astrofate_push_ask_dismissed_at";
const DISMISS_TTL_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

export default function NotificationOptIn({ userRole }) {
  const { lang } = useI18n();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    if (!isPushSupported() || !isPushConfigured()) return;
    // If permission is already granted from a prior session but we haven't
    // yet registered a token on THIS device (localStorage flag cleared, e.g.
    // reinstalled the APK), silently register in the background — no UI.
    if (currentPermission() === "granted" && !hasOptedInBefore()) {
      enablePushNotifications().catch(() => {});
      return;
    }
    if (currentPermission() !== "default") return;
    if (hasOptedInBefore()) return;
    try {
      const raw = window.localStorage.getItem(DISMISS_KEY);
      const dismissedAt = raw ? parseInt(raw, 10) : 0;
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;
    } catch { /* private mode */ }
    setVisible(true);
  }, []);

  if (!visible) return null;

  const handleEnable = async () => {
    setBusy(true);
    const token = await enablePushNotifications();
    setBusy(false);
    if (token) {
      setFlash({ ok: true, msg: lang === "hi" ? "Notifications चालू हो गए ✨" : "Notifications enabled ✨" });
      setTimeout(() => setVisible(false), 1500);
    } else {
      setFlash({
        ok: false,
        msg:
          lang === "hi"
            ? "Enable नहीं हो पाया। Browser settings में Notifications allow करें।"
            : "Couldn't enable. Please allow notifications in your browser settings.",
      });
    }
  };

  const handleDismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* no-op */ }
    setVisible(false);
  };

  const title =
    userRole === "admin"
      ? (lang === "hi" ? "नई chat requests के लिए alerts चाहिए?" : "Get alerts for new chat requests?")
      : (lang === "hi" ? "अपने astrologer के जवाब सबसे पहले सुनें" : "Hear from your astrologer first");
  const desc =
    userRole === "admin"
      ? (lang === "hi"
          ? "जब कोई customer message करे या booking करे तो instant push notification मिलेगी।"
          : "Get an instant push whenever a customer messages or books a consultation.")
      : (lang === "hi"
          ? "जब आपका astrologer reply करे या plan expire होने वाला हो तो instant alert मिलेगा।"
          : "We'll ping you the moment your astrologer replies or your plan is about to expire.");
  const cta = lang === "hi" ? "Notifications enable करें" : "Enable Notifications";
  const later = lang === "hi" ? "बाद में" : "Not now";

  return (
    <div
      data-testid="notification-opt-in"
      className="glass-card animate-fadeInUp"
      style={{
        padding: "18px 20px",
        marginBottom: "20px",
        border: "1px solid rgba(255,209,102,0.28)",
        background: "linear-gradient(135deg, rgba(255,209,102,0.10), rgba(91,30,140,0.10))",
        display: "flex",
        alignItems: "center",
        gap: "16px",
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          width: "44px",
          height: "44px",
          borderRadius: "12px",
          background: "linear-gradient(135deg, #F4B942, #FFD166)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Bell size={20} color="#120021" />
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ color: "#FFD166", fontWeight: 700, fontSize: "0.95rem", marginBottom: 2 }}>{title}</div>
        <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.82rem", lineHeight: 1.5 }}>{desc}</div>
        {flash && (
          <div
            data-testid="notification-opt-in-flash"
            style={{
              marginTop: 8,
              fontSize: "0.78rem",
              color: flash.ok ? "#86efac" : "#fca5a5",
            }}
          >
            {flash.msg}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <button
          data-testid="notification-opt-in-later"
          onClick={handleDismiss}
          disabled={busy}
          className="btn-ghost"
          style={{ padding: "8px 14px", fontSize: "0.82rem" }}
        >
          {later}
        </button>
        <button
          data-testid="notification-opt-in-accept"
          onClick={handleEnable}
          disabled={busy}
          className="btn-gold"
          style={{ padding: "9px 16px", fontSize: "0.82rem", display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          <Bell size={13} />
          {busy ? "…" : cta}
        </button>
        <button
          data-testid="notification-opt-in-close"
          onClick={handleDismiss}
          disabled={busy}
          aria-label="Close"
          style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", cursor: "pointer", padding: 4 }}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
